import { type intlFormatPropTypes } from "react-intl";
import { z } from "zod";

import { addressFormMessages } from "../../../AddressForm/AddressForm.messages";
import type { Country } from "../../../AddressForm/AddressForm.types";
import {
  createAddressRules,
  isBlank,
} from "../../../AddressForm/AddressForm.utils";
import {
  TAX_DETAILS_ADDRESS_FIELDS,
  TAX_DETAILS_MAX_TAX_RATE,
} from "../TaxDetailsFormSection.consts";
import { TAX_DETAILS_FORM_ERROR_MESSAGES } from "../TaxDetailsFormSection.messages";
import type { TaxDetailsFormValues } from "../TaxDetailsFormSection.types";
import { isTaxIdValid } from "../TaxDetailsFormSection.utils";

/** Shape only — the rules below depend on the country and on who is filling it in. */
const taxDetailsFormShape = z.object({
  businessName: z.string(),
  taxId: z.string(),
  taxRate: z.string(),
  country: z.string(),
  street: z.string(),
  unit: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  isConfirmed: z.boolean(),
}) satisfies z.ZodType<TaxDetailsFormValues>;

type CreateTaxDetailsFormSchemaArgs = {
  /**
   * False when the selected region levies no tax for this scope. The ID and rate
   * fields are not rendered then, so validating them would report errors against
   * fields nobody can see.
   */
  hasScopedTax: boolean;
  intl: Pick<intlFormatPropTypes, "formatMessage">;
  /** Hosts declare a rate; guests do not, so the field is not validated for them. */
  isHost: boolean;
  /** Decides whether the ID is checked against the nation's format or a province's. */
  isSubregion: boolean;
  /** The country currently picked in the form, from `@shopify/address`. */
  selectedCountry?: Country;
  /**
   * Names the tax in every error, already translated — "VAT", "GST/HST",
   * "Sales tax". Goes straight into the messages' `{taxType}` placeholder.
   */
  taxTypeName: string;
};

/**
 * A factory, not a constant: the country decides the address rules and the ID
 * format, and who is filling the form in decides whether a rate is required.
 */
const createTaxDetailsFormSchema = ({
  hasScopedTax,
  intl,
  isHost,
  isSubregion,
  selectedCountry,
  taxTypeName,
}: CreateTaxDetailsFormSchemaArgs) =>
  taxDetailsFormShape.superRefine((values, ctx) => {
    const addIssue = (path: keyof TaxDetailsFormValues, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });

    if (isBlank(values.businessName)) {
      addIssue(
        "businessName",
        intl.formatMessage(
          TAX_DETAILS_FORM_ERROR_MESSAGES.businessNameRequired,
        ),
      );
    }

    const hasValidTaxId = isTaxIdValid({
      country: values.country,
      isSubregion,
      state: values.state,
      taxId: values.taxId,
    });

    if (hasScopedTax && !hasValidTaxId) {
      addIssue(
        "taxId",
        intl.formatMessage(TAX_DETAILS_FORM_ERROR_MESSAGES.taxIdInvalid, {
          country: selectedCountry?.name ?? "",
          taxType: taxTypeName,
        }),
      );
    }

    if (hasScopedTax && isHost) {
      const taxRate = Number(values.taxRate);
      const isTaxRateValid =
        !isBlank(values.taxRate) &&
        Number.isFinite(taxRate) &&
        0 <= taxRate &&
        taxRate < TAX_DETAILS_MAX_TAX_RATE;

      if (!isTaxRateValid) {
        addIssue(
          "taxRate",
          intl.formatMessage(TAX_DETAILS_FORM_ERROR_MESSAGES.taxRateInvalid, {
            maxRate: TAX_DETAILS_MAX_TAX_RATE,
            taxType: taxTypeName,
          }),
        );
      }
    }

    if (isBlank(values.country)) {
      addIssue(
        "country",
        intl.formatMessage(addressFormMessages.selectCountry),
      );
    }

    createAddressRules({
      fields: TAX_DETAILS_ADDRESS_FIELDS,
      intl,
      selectedCountry,
    })(values, ctx);

    if (!values.isConfirmed) {
      addIssue(
        "isConfirmed",
        intl.formatMessage(
          TAX_DETAILS_FORM_ERROR_MESSAGES.confirmationRequired,
        ),
      );
    }
  });

export { createTaxDetailsFormSchema };
